import React, { FC, useMemo } from 'react'
import { CrewDeduction, DeductionStatus } from '../core/_requests'
import VesselLandingLink from '../../../shared/components/VesselLandingLink'

type Props = {
    visible: boolean
    claim: CrewDeduction | null
    onClose: () => void
    onViewAttachments?: () => void
}

const WRAP_STYLE: React.CSSProperties = {
    background: 'rgba(0,0,0,0.5)',
    position: 'fixed',
    inset: 0,
    zIndex: 1050,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
}

const LABEL_SMALL = 'fw-semibold text-muted d-block mb-1'

const getStatusDisplay = (status: DeductionStatus) => {
    switch (status) {
        case 'DRAFT':
            return { label: 'Draft', badgeClass: 'badge-light-secondary' }
        case 'SUBMITTED':
            return { label: 'Submitted', badgeClass: 'badge-light-warning' }
        case 'REVIEWED':
            return { label: 'Reviewed', badgeClass: 'badge-light-success' }
        case 'REJECTED':
            return { label: 'Rejected', badgeClass: 'badge-light-danger' }
        default:
            return { label: String(status), badgeClass: 'badge-light' }
    }
}

const formatCurrency = (value: number) =>
    value.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

const formatDate = (dateStr: string | null | undefined) => {
    if (!dateStr) return '-'
    try {
        const date = new Date(dateStr)
        return date.toLocaleDateString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
        })
    } catch {
        return dateStr
    }
}

const formatClaimMonth = (dateStr: string | null | undefined) => {
    if (!dateStr) return '-'
    try {
        const date = new Date(dateStr)
        return date.toLocaleDateString('en-IN', {
            month: 'long',
            year: 'numeric',
        })
    } catch {
        return dateStr
    }
}

export const ViewCrewDeductionModal: FC<Props> = ({
    visible,
    claim,
    onClose,
    onViewAttachments,
}) => {
    const statusMeta = useMemo(() => {
        if (!claim) return { label: '-', badgeClass: 'badge-light' }
        return getStatusDisplay(claim.status)
    }, [claim])

    if (!visible || !claim) return null

    return (
        <div style={WRAP_STYLE} tabIndex={-1}>
            <div
                className='bg-white text-dark rounded shadow-lg d-flex flex-column'
                style={{
                    width: '75vw',
                    height: '75vh',
                    maxWidth: '1100px',
                    maxHeight: '800px',
                }}
            >
                {/* Header */}
                <div className='d-flex align-items-center justify-content-between border-bottom px-4 py-3 flex-shrink-0'>
                    <div className='d-flex flex-column'>
                        <h5 className='modal-title text-dark m-6'>
                            Crew Deduction – {claim.crewName || `Deduction #${claim.id}`}
                        </h5>
                        <div className='text-muted fs-7 mx-6'>
                            Rank:&nbsp;
                            <span className='fw-semibold'>
                                {claim.rankName || '-'}
                            </span>
                            {claim.vesselName && (
                                <>
                                    <span className='mx-2'>•</span>
                                    Vessel:&nbsp;
                                    <VesselLandingLink
                                        vesselId={claim.vesselId ?? null}
                                        vesselName={claim.vesselName}
                                        className='fw-semibold text-dark text-hover-primary'
                                    />
                                </>
                            )}
                            {claim.claimMonth && (
                                <>
                                    <span className='mx-2'>•</span>
                                    Claim Month:&nbsp;
                                    <span className='fw-semibold'>
                                        {formatClaimMonth(claim.claimMonth)}
                                    </span>
                                </>
                            )}
                        </div>
                    </div>

                    <div className='d-flex align-items-center gap-3'>
                        <span className={`badge ${statusMeta.badgeClass}`}>
                            {statusMeta.label}
                        </span>

                        <button
                            type='button'
                            className='btn-close m-6'
                            aria-label='Close'
                            onClick={onClose}
                        />
                    </div>
                </div>

                {/* Body with scroll */}
                <div className='flex-grow-1 overflow-auto px-4 py-3 m-6'>
                    <div className='row g-4'>
                        {/* Deduction Details Section */}
                        <div className='col-12'>
                            <h6 className='text-primary mb-3 border-bottom pb-2'>Deduction Details</h6>

                            <div className='row'>
                                <div className='col-md-4 mb-3'>
                                    <label className={LABEL_SMALL}>Crew Name</label>
                                    <div className='fw-semibold'>{claim.crewName || '-'}</div>
                                </div>
                                <div className='col-md-4 mb-3'>
                                    <label className={LABEL_SMALL}>Rank</label>
                                    <div>{claim.rankName || '-'}</div>
                                </div>
                                <div className='col-md-4 mb-3'>
                                    <label className={LABEL_SMALL}>Deduction Month</label>
                                    <div>{formatClaimMonth(claim.claimMonth)}</div>
                                </div>
                            </div>
                        </div>

                        {/* Deductions Section */}
                        <div className='col-12'>
                            <h6 className='text-primary mb-3 border-bottom pb-2'>Deductions</h6>

                            <div className='row'>
                                <div className='col-md-3 mb-3'>
                                    <label className={LABEL_SMALL}>Union / Social dues</label>
                                    <div>{claim.unionSocialDues || 'As per CBA'}</div>
                                </div>
                                <div className='col-md-3 mb-3'>
                                    <label className={LABEL_SMALL}>Cash Advance</label>
                                    <div>{formatCurrency(claim.deductionCashAdvanceFromOffice || 0)}</div>
                                </div>
                                <div className='col-md-2 mb-3'>
                                    <label className={LABEL_SMALL}>Bond</label>
                                    <div>{formatCurrency(claim.deductionBond || 0)}</div>
                                </div>
                                <div className='col-md-2 mb-3'>
                                    <label className={LABEL_SMALL}>Other Deductions</label>
                                    <div>{formatCurrency(claim.deductionOtherDeductions || 0)}</div>
                                </div>
                                <div className='col-md-2 mb-3'>
                                    <label className={LABEL_SMALL}>Internet Card</label>
                                    <div>{formatCurrency(claim.deductionInternetCard || 0)}</div>
                                </div>
                            </div>

                            <div className='row'>
                                <div className='col-md-6'>
                                    <div className='border rounded p-3 bg-light-primary'>
                                        {(claim.firstTimeDeduction || 0) > 0 && (
                                            <>
                                                <label className={LABEL_SMALL}>1st time deduction</label>
                                                <div className='fs-5 fw-bold text-primary mb-2'>
                                                    {formatCurrency(claim.firstTimeDeduction || 0)}
                                                </div>
                                            </>
                                        )}
                                        <label className={LABEL_SMALL}>Monthly Deduction Total</label>
                                        <div className='fs-3 fw-bold text-primary'>
                                            {formatCurrency(claim.deductionsTotal || 0)}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Remarks Section */}
                        <div className='col-12'>
                            <h6 className='text-primary mb-3 border-bottom pb-2'>Remarks</h6>
                            <div
                                className='border rounded p-3 bg-light'
                                style={{ minHeight: '60px', whiteSpace: 'pre-wrap' }}
                            >
                                {claim.remarks || 'No remarks provided.'}
                            </div>
                            <div
                                className='border rounded p-3 bg-light mt-3'
                                style={{ minHeight: '60px', whiteSpace: 'pre-wrap' }}
                            >
                                <span className='fw-semibold d-block mb-2'>Office Remarks</span>
                                {claim.officeRemarks || 'No office remarks provided.'}
                            </div>
                        </div>

                        {/* Status & Audit Info Section */}
                        <div className='col-12'>
                            <h6 className='text-primary mb-3 border-bottom pb-2'>Status & History</h6>
                            <div className='row'>
                                <div className='col-md-3 mb-2'>
                                    <label className={LABEL_SMALL}>Status</label>
                                    <div>
                                        <span className={`badge ${statusMeta.badgeClass}`}>
                                            {statusMeta.label}
                                        </span>
                                    </div>
                                </div>
                                <div className='col-md-3 mb-2'>
                                    <label className={LABEL_SMALL}>Submitted At</label>
                                    <div>{formatDate(claim.submittedAt)}</div>
                                </div>
                                <div className='col-md-3 mb-2'>
                                    <label className={LABEL_SMALL}>Reviewed At</label>
                                    <div>{formatDate(claim.reviewedAt)}</div>
                                </div>
                                <div className='col-md-3 mb-2'>
                                    <label className={LABEL_SMALL}>Reviewed By</label>
                                    <div>{claim.reviewedByName || '-'}</div>
                                </div>
                            </div>

                            {claim.status === 'REJECTED' && claim.rejectionReason && (
                                <div className='row mt-2'>
                                    <div className='col-12'>
                                        <label className={LABEL_SMALL}>Rejection Reason</label>
                                        <div
                                            className='border rounded p-2 bg-light-danger text-danger'
                                            style={{ whiteSpace: 'pre-wrap' }}
                                        >
                                            {claim.rejectionReason}
                                        </div>
                                    </div>
                                </div>
                            )}

                            <div className='row mt-3'>
                                <div className='col-md-6 mb-2'>
                                    <label className={LABEL_SMALL}>Created At</label>
                                    <div>{formatDate(claim.createdAt)}</div>
                                </div>
                                <div className='col-md-6 mb-2'>
                                    <label className={LABEL_SMALL}>Last Updated</label>
                                    <div>{formatDate(claim.updatedAt)}</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className='border-top d-flex justify-content-end gap-2 px-5 py-4 flex-shrink-0'>
                    <button type='button' className='btn btn-light btn-sm' onClick={onClose}>
                        Close
                    </button>
                </div>
            </div>
        </div>
    )
}

export default ViewCrewDeductionModal

